/**
 * §A.7 · Las reclamaciones de ficha.
 *
 * ## El reloj es el contenido de esta pantalla
 *
 * «Un maestro que reclama su ficha un martes y sigue esperando el viernes ya no
 * vuelve.» Así que la espera va delante, en palabras y no en horas crudas, y la lista
 * entra por las pendientes.
 *
 * ## Aprobar crea el dueño y su cuenta
 *
 * No es «marcar como aprobada»: crea la cuenta de quien reclamó y le pasa el club. Se
 * confirma y se dice, porque es irreversible por esta vía.
 *
 * ## Rechazar exige motivo, y el contrato dice por qué
 *
 * «Obligatorio y con fondo. La persona que reclamó va a preguntar por qué, y "no
 * procede" tres meses después no lo explica ni a ella ni a quien lo mire desde
 * dentro.» El servidor pide diez caracteres; la pantalla lo pide antes y lo explica.
 *
 * ## Lo que se mira para decidir es `evidence`
 *
 * Es un `Record<string, unknown>` —lo que haya aportado quien reclama— y se pinta tal
 * cual, sin darle forma: inventarle una estructura sería prometer una que el servidor
 * no promete, y aquí lo que hace falta es leerlo.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import {
  esperaEnPalabras,
  type Reclamacion,
} from '@/backoffice/tiposDeDirectorio';

/** El mínimo del DTO. Se dice antes de enviar en vez de esperar el 422. */
const MINIMO_MOTIVO = 10;

export function Reclamaciones({
  alAbrirClub,
}: {
  alAbrirClub: (clubId: string) => void;
}) {
  const cache = useQueryClient();
  const [rechazando, setRechazando] = useState<string | null>(null);
  const [aprobando, setAprobando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const reclamaciones = useQuery({
    queryKey: ['admin', 'reclamaciones'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['academy-claims'].get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Reclamacion[];
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['admin', 'reclamaciones'] });
    void cache.invalidateQueries({ queryKey: ['admin', 'directorio'] });
    void cache.invalidateQueries({ queryKey: ['admin', 'clubes'] });
  };

  const aprobar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.admin['academy-claims']({ id }).approve.post(
        {},
        // Crea una cuenta y manda una invitación: clave nueva por pulsación.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setAprobando(null);
      refrescar();
    },
    onError: (e) => {
      setAprobando(null);
      setProblema(aProblema(e));
    },
  });

  const rechazar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.admin['academy-claims']({ id }).reject.post({
        reason: motivo.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRechazando(null);
      setMotivo('');
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (reclamaciones.isPending)
    return <Cargando filas={4} etiqueta="Cargando" />;

  if (reclamaciones.isError || !reclamaciones.data) {
    return (
      <PanelError
        problema={aProblema(reclamaciones.error)}
        reintentar={() => void reclamaciones.refetch()}
      />
    );
  }

  const pendientes = reclamaciones.data.filter((r) => r.status === 'pending');
  const decididas = reclamaciones.data.filter((r) => r.status !== 'pending');

  return (
    <section>
      <h1>Fichas reclamadas</h1>

      <p className="tenue">
        Maestros que dicen que un club del directorio es el suyo.{' '}
        <strong>
          Uno que reclama un martes y sigue esperando el viernes ya no vuelve.
        </strong>
      </p>

      {problema && <PanelError problema={problema} />}

      {pendientes.length === 0 ? (
        <Vacio
          titulo="Nada pendiente"
          explicacion="Es la buena noticia de esta pantalla: no hay nadie esperando a que le devolvamos su club."
        />
      ) : (
        <ul className="papeles">
          {pendientes.map((r) => (
            <li
              key={r.id}
              /* Más de 24 horas esperando ya es tarde: se marca la fila. */
              className={
                r.hours_waiting !== null && r.hours_waiting >= 24
                  ? 'fila-marcada'
                  : undefined
              }
            >
              <div className="aviso-cabeza">
                <strong>{r.academy_name}</strong>
                <span className="pastilla pastilla-left">
                  Esperando {esperaEnPalabras(r.hours_waiting)}
                </span>
                <code className="tenue">{r.academy_slug}</code>
              </div>

              <p>
                Lo reclama <strong>{r.claimant_name}</strong> ·{' '}
                {r.claimant_email}
              </p>

              {Object.keys(r.evidence).length > 0 && (
                <dl className="datos">
                  {Object.entries(r.evidence).map(([k, v]) => (
                    <div key={k}>
                      <dt>{k}</dt>
                      {/* Lo que haya aportado, tal cual: aquí lo que hace falta es leerlo. */}
                      <dd>{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <p className="tenue">Pedida el {fechaYHora(r.created_at)}</p>

              {/* ── Aprobar ─────────────────────────────────────────────── */}
              {aprobando === r.id ? (
                <div className="aviso" role="alert">
                  <p>
                    <strong>
                      Esto le crea la cuenta a {r.claimant_name} y le pasa el
                      club.
                    </strong>{' '}
                    Recibirá un correo para entrar, y desde ese momento «
                    {r.academy_name}» es suyo.
                  </p>
                  <div className="hueco-acciones">
                    <button
                      type="button"
                      disabled={aprobar.isPending}
                      onClick={() => aprobar.mutate(r.id)}
                    >
                      {aprobar.isPending ? 'Aprobando…' : 'Sí, es su club'}
                    </button>
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => setAprobando(null)}
                    >
                      No
                    </button>
                  </div>
                </div>
              ) : rechazando === r.id ? (
                <div className="hueco">
                  <label>
                    Por qué se rechaza
                    <input
                      maxLength={300}
                      placeholder="El club ya tiene dueño y no es esta persona."
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                  </label>
                  <p className="tenue">
                    {motivo.trim().length < MINIMO_MOTIVO ? (
                      <strong>
                        Hace falta un motivo con fondo, de al menos{' '}
                        {MINIMO_MOTIVO} caracteres. Quien reclamó va a preguntar
                        por qué, y «no procede» no lo explica ni a él ni a quien
                        lo mire desde dentro dentro de tres meses.
                      </strong>
                    ) : (
                      'Se le manda tal cual, así que escríbelo como se lo dirías.'
                    )}
                  </p>
                  <div className="hueco-acciones">
                    <button
                      type="button"
                      disabled={
                        rechazar.isPending ||
                        motivo.trim().length < MINIMO_MOTIVO
                      }
                      onClick={() => rechazar.mutate(r.id)}
                    >
                      {rechazar.isPending ? 'Rechazando…' : 'Rechazar'}
                    </button>
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => {
                        setRechazando(null);
                        setMotivo('');
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="aviso-pie">
                  <button
                    type="button"
                    className="btn-plano"
                    onClick={() => alAbrirClub(r.academy_id)}
                  >
                    Ver el club
                  </button>
                  <div className="hueco-acciones">
                    <button
                      type="button"
                      onClick={() => {
                        setProblema(null);
                        setAprobando(r.id);
                      }}
                    >
                      Es su club
                    </button>
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => {
                        setProblema(null);
                        setMotivo('');
                        setRechazando(r.id);
                      }}
                    >
                      No lo es
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {decididas.length > 0 && (
        <>
          <h2 className="seccion">Ya decididas</h2>
          <ul className="papeles">
            {decididas.map((r) => (
              <li key={r.id} className="fila-fuera">
                <div className="aviso-cabeza">
                  <strong>{r.academy_name}</strong>
                  <span
                    className={`pastilla ${
                      r.status === 'approved'
                        ? 'pastilla-bien'
                        : 'pastilla-paused'
                    } pastilla-left`}
                  >
                    {r.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                  </span>
                </div>
                <p className="tenue">
                  {r.claimant_email} ·{' '}
                  {r.decided_at ? fechaYHora(r.decided_at) : '—'}
                </p>
                {r.rejection_reason && <p>{r.rejection_reason}</p>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
